<script setup lang="ts">
import { computed, ref, watch } from "vue";
import BbelField from "@/views/builder/components/bbel/BbelField.vue";
import BodyEditorText from "@/views/builder/components/service-models/http/BodyEditorText.vue";
import BbelMapRows from "./BbelMapRows.vue";
import { HANDLER_NAMES, type OutboundSpec, type TemplateAuthFlowSpec, type TemplateHandlerSpec } from "@/types";
import type { TemplateScopes } from "../composables/useConnectionTemplateScope";

const props = defineProps<{
  modelValue: TemplateAuthFlowSpec;
  /** Per-phase BBEL scopes: request fields, onResponse values, per-call outbound. */
  scopes: TemplateScopes;
  /** The method's declared connection field names — feeds the onResponse key suggestions. */
  connectionFields: string[];
}>();

const emit = defineEmits<{
  "update:modelValue": [value: TemplateAuthFlowSpec];
}>();

function updateFlow(patch: Partial<TemplateAuthFlowSpec>) {
  emit("update:modelValue", { ...props.modelValue, ...patch });
}

const kind = computed(() => props.modelValue.kind);

// Token-bearing kinds have a setup pass writing token fields; the rest are per-call auth.
const TOKEN_BEARING = ["oauth2-authcode", "oauth2-clientcreds", "oauth2-password", "jwt-bearer", "vendor-initiated"];
const isTokenBearing = computed(() => TOKEN_BEARING.includes(kind.value));

// ── Handler vocabulary for the menu: display labels ────────────────────────────────────────────
// Labels are UI vocabulary (Title Case); the lowercase wire names live in the JSON and anywhere
// an author references them (the not-added pane's <code> tag).

const HANDLER_LABELS: Record<string, string> = {
  authorize: "Authorize",
  token: "Token",
  info: "Info",
  refresh: "Refresh",
  invalidate: "Invalidate",
  test: "Test",
};

/** One operative sentence per handler — the card header's context strip. */
const HANDLER_HELP: Record<string, string> = {
  authorize:
    "Browser step — the user is sent to this URL. Pass {{ system.callbackUrl }} as the redirect_uri; the vendor redirects back there to continue setup.",
  token: "Server-to-server — runs on the platform after setup starts, exchanging the code or credentials for a token. Store it via On Response.",
  info: "Runs after the token step to verify the connection and capture vendor metadata.",
  refresh: "Runs when the stored token expires. Store the renewed token via On Response.",
  invalidate: "On demand — revokes the token at the vendor when a connection is revoked or deleted.",
  test: "On demand — connectivity check from the connection screen.",
};

/** Handlers meaningful for the kind — mirrors AuthFlowKinds.SetupStyleOf (advisory; the server
 *  publish gate stays authority). Anything already declared is always shown, never hidden. */
function relevantHandlers(k: string): string[] {
  if (k === "oauth2-authcode") return [...HANDLER_NAMES];
  if (TOKEN_BEARING.includes(k)) return HANDLER_NAMES.filter((n) => n !== "authorize");
  return ["test"];
}

const declared = computed(() => new Set(Object.keys(props.modelValue.handlers ?? {})));

// Flat, lifecycle-ordered (HANDLER_NAMES order tells the flow story).
const menuHandlers = computed(() => {
  const relevant = new Set(relevantHandlers(kind.value));
  return HANDLER_NAMES.filter((n) => relevant.has(n) || declared.value.has(n));
});

// ── Handlers CRUD ──────────────────────────────────────────────────────────────────────────────

const HTTP_METHODS = ["GET", "POST", "PUT", "PATCH", "DELETE"];

function patchHandler(name: string, patch: Partial<TemplateHandlerSpec>) {
  const current = props.modelValue.handlers?.[name];
  if (!current) return;
  updateFlow({ handlers: { ...props.modelValue.handlers, [name]: { ...current, ...patch } } });
}

function patchRequest(name: string, patch: Partial<TemplateHandlerSpec["request"]>) {
  const current = props.modelValue.handlers?.[name];
  if (!current) return;
  patchHandler(name, { request: { ...current.request, ...patch } });
}

function addHandler(name: string) {
  // Token-bearing handlers arrive pre-seeded with the reserved token targets (ADR-022) — ordinary
  // rows the author fills or deletes; unfilled ones are pruned at the save boundary.
  const seedTokenRows = isTokenBearing.value && (name === "token" || name === "refresh");
  const handler: TemplateHandlerSpec = {
    request: { url: "" },
    ...(seedTokenRows
      ? {
          onResponse: {
            "connection.accessToken": "",
            "connection.refreshToken": "",
            "connection.expiresAt": "",
          },
        }
      : {}),
  };
  updateFlow({ handlers: { ...(props.modelValue.handlers ?? {}), [name]: handler } });
  selected.value = name;
}

// Structure removals confirm (rows don't); the removal stays local until Save Changes.
const confirmRemoveHandler = ref<string | null>(null);

function removeHandler(name: string) {
  const handlers = { ...(props.modelValue.handlers ?? {}) };
  delete handlers[name];
  updateFlow({ handlers: Object.keys(handlers).length ? handlers : null });
  confirmRemoveHandler.value = null;
}

// ── Selection: one pane at a time, navigated by a native grouped select ────────────────────────

/** A handler name (declared or not — undeclared panes carry the Add CTA), "outbound", or "settings". */
const selected = ref("");

const hasSettings = computed(() => kind.value === "oauth2-authcode");

const selectable = computed(() => [
  ...menuHandlers.value,
  "outbound",
  ...(hasSettings.value ? ["settings"] : []),
]);

watch(
  selectable,
  (s) => {
    if (!s.includes(selected.value))
      selected.value = s.find((n) => declared.value.has(n)) ?? "outbound";
  },
  { immediate: true },
);

const activeHandler = computed(() =>
  selected.value !== "outbound" && props.modelValue.handlers?.[selected.value]
    ? { name: selected.value, handler: props.modelValue.handlers[selected.value] }
    : null,
);

// ── onResponse key vocabulary + outbound ───────────────────────────────────────────────────────

// Valid write targets for the key datalist: the reserved token names, the method's declared
// fields, and the open flow.* namespace (suggested as a stub).
const writeTargets = computed(() => [
  "connection.accessToken",
  "connection.refreshToken",
  "connection.expiresAt",
  ...props.connectionFields.filter((f) => f.trim()).map((f) => `connection.${f}`),
  "flow.",
]);

const ON_RESPONSE_TOOLTIP =
  "Each write evaluates against this handler's response. Reserved targets: connection.accessToken " +
  "(required on token/refresh — stored encrypted, the handler fails if it resolves empty), " +
  "connection.refreshToken (stored encrypted; the stored value is kept when the response omits it), " +
  "connection.expiresAt (drives renewal timing). Other connection.* keys must be declared fields; " +
  "flow.* carries values to later handlers in the setup pass.";

function patchOutbound(patch: Partial<OutboundSpec>) {
  const merged = { ...(props.modelValue.outbound ?? {}), ...patch };
  const empty = !merged.headers && !merged.query;
  updateFlow({ outbound: empty ? null : merged });
}
</script>

<template>
  <div>
    <!-- The whole inventory is visible: flat, lifecycle-ordered pills, added state scannable
         without opening anything. A dimmed pill with a plus is declarable — its pane carries
         the Add CTA. -->
    <ul class="nav nav-pills gap-1 mb-3 align-items-center">
      <li v-for="name in menuHandlers" :key="name" class="nav-item">
        <button
          type="button"
          class="nav-link py-1 px-2"
          :class="{ active: selected === name }"
          @click="selected = name"
        >
          <i
            v-if="declared.has(name)"
            class="bi bi-check2 me-1"
            :class="{ 'text-success': selected !== name }"
          ></i>
          <i v-else class="bi bi-plus me-1" :class="{ 'text-muted': selected !== name }"></i>
          <span :class="{ 'text-muted': !declared.has(name) && selected !== name }">
            {{ HANDLER_LABELS[name] }}
          </span>
        </button>
      </li>
      <li class="vr mx-1" role="presentation"></li>
      <li class="nav-item">
        <button
          type="button"
          class="nav-link py-1 px-2"
          :class="{ active: selected === 'outbound' }"
          @click="selected = 'outbound'"
        >
          Outbound
        </button>
      </li>
      <li v-if="hasSettings" class="nav-item">
        <button
          type="button"
          class="nav-link py-1 px-2"
          :class="{ active: selected === 'settings' }"
          @click="selected = 'settings'"
        >
          Settings
        </button>
      </li>
    </ul>

    <!-- Handler pane, keyed so BbelMapRows' local rows re-seed per handler. The card header is
         the context strip: what this step does, and where it can be removed. -->
    <div v-if="activeHandler" :key="selected" class="card">
      <div class="card-header">
        <span class="text-muted small">{{ HANDLER_HELP[activeHandler.name] }}</span>
      </div>
      <div class="card-body">

      <div class="d-flex align-items-stretch gap-2 mb-3">
        <select
          class="form-select w-auto flex-shrink-0"
          :value="activeHandler.handler.request.method ?? 'GET'"
          @change="patchRequest(activeHandler.name, { method: ($event.target as HTMLSelectElement).value })"
        >
          <option v-for="m in HTTP_METHODS" :key="m" :value="m">{{ m }}</option>
        </select>
        <BbelField
          class="flex-grow-1"
          :model-value="activeHandler.handler.request.url"
          :scope="scopes.request"
          single-line
          placeholder="https://vendor.com/oauth/token"
          @update:model-value="patchRequest(activeHandler.name, { url: $event })"
        />
      </div>

      <BbelMapRows
        :model-value="activeHandler.handler.request.headers"
        :scope="scopes.request"
        label="Headers"
        add-label="Add Header"
        key-placeholder="Header name"
        value-placeholder="Header value"
        @update:model-value="patchRequest(activeHandler.name, { headers: $event })"
      />
      <BbelMapRows
        :model-value="activeHandler.handler.request.query"
        :scope="scopes.request"
        label="Query Parameters"
        add-label="Add Parameter"
        key-placeholder="Parameter name"
        value-placeholder="Parameter value"
        @update:model-value="patchRequest(activeHandler.name, { query: $event })"
      />

      <div class="mb-3 col-md-5">
        <label class="form-label">
          Content Type
          <i
            class="bi bi-info-circle text-muted ms-1"
            data-bs-toggle="tooltip"
            title="The body's media type. Declared here, not as a Content-Type header — publish rejects content headers in the header rows."
          ></i>
        </label>
        <input
          type="text"
          class="form-control font-monospace"
          list="handler-content-types"
          placeholder="application/json"
          :value="activeHandler.handler.request.contentType ?? ''"
          @input="patchRequest(activeHandler.name, { contentType: ($event.target as HTMLInputElement).value || null })"
        />
        <datalist id="handler-content-types">
          <option value="application/json"></option>
          <option value="application/xml"></option>
          <option value="application/soap+xml"></option>
          <option value="application/x-www-form-urlencoded"></option>
          <option value="text/xml"></option>
          <option value="text/plain"></option>
        </datalist>
      </div>

      <BodyEditorText
        label="Body"
        :model-value="activeHandler.handler.request.body ?? ''"
        :content-type="activeHandler.handler.request.contentType ?? ''"
        :scope="scopes.request"
        placeholder="Request body (literal or BBEL)"
        @update:model-value="patchRequest(activeHandler.name, { body: $event || null })"
      />

      <BbelMapRows
        :model-value="activeHandler.handler.onResponse"
        :scope="scopes.response"
        label="On Response"
        add-label="Add Write"
        key-placeholder="scope.field"
        value-placeholder="BBEL over response.*"
        :key-options="writeTargets"
        :label-tooltip="ON_RESPONSE_TOOLTIP"
        @update:model-value="patchHandler(activeHandler.name, { onResponse: $event })"
      />
      </div>
      <!-- Object commands sit in the footer — the card is a mini-form (Action Button Rules). -->
      <div class="card-footer d-flex justify-content-end">
        <button
          type="button"
          class="bb-btn-danger btn-sm"
          @click="confirmRemoveHandler = activeHandler.name"
        >
          <i class="bi bi-trash"></i> Remove
        </button>
      </div>
    </div>

    <div v-else-if="selected === 'outbound'" class="card">
      <div class="card-header">
        <span class="text-muted small">Applied to every outbound call a workflow makes on this method, once authenticated.</span>
      </div>
      <div class="card-body">
      <BbelMapRows
        :model-value="modelValue.outbound?.headers"
        :scope="scopes.outbound"
        label="Headers"
        add-label="Add Header"
        key-placeholder="Header name"
        value-placeholder="Header value"
        @update:model-value="patchOutbound({ headers: $event })"
      />
      <BbelMapRows
        :model-value="modelValue.outbound?.query"
        :scope="scopes.outbound"
        label="Query Parameters"
        add-label="Add Parameter"
        key-placeholder="Parameter name"
        value-placeholder="Parameter value"
        @update:model-value="patchOutbound({ query: $event })"
      />
      </div>
    </div>

    <div v-else-if="selected === 'settings'" class="card">
      <div class="card-header">
        <span class="text-muted small">Flow-level options for this auth kind.</span>
      </div>
      <div class="card-body">
      <div class="row g-3">
        <div class="col-12 col-md-3">
          <label class="form-label">
            PKCE
            <i
              class="bi bi-info-circle text-muted ms-1"
              data-bs-toggle="tooltip"
              title="Proof Key for Code Exchange — the engine mints and carries the verifier when enabled."
            ></i>
          </label>
          <select
            class="form-select"
            :value="modelValue.pkce ?? ''"
            @change="updateFlow({ pkce: ($event.target as HTMLSelectElement).value || null })"
          >
            <option value="">Off</option>
            <option value="S256">S256</option>
          </select>
        </div>
      </div>
      </div>
    </div>

    <!-- A selectable-but-undeclared handler: the card carries the CTA the menu deliberately doesn't. -->
    <div v-else class="card">
      <div class="card-body">
        <span class="text-muted">
          The <code>{{ selected }}</code> handler isn't defined on this method. {{ HANDLER_HELP[selected] }}
        </span>
      </div>
      <div class="card-footer d-flex justify-content-end">
        <button type="button" class="btn btn-outline-primary btn-sm" @click="addHandler(selected)">
          <i class="bi bi-plus"></i> Add {{ HANDLER_LABELS[selected] ?? selected }} handler
        </button>
      </div>
    </div>

    <div
      class="modal fade"
      :class="{ show: confirmRemoveHandler !== null }"
      :style="{ display: confirmRemoveHandler !== null ? 'block' : 'none' }"
      @click.self="confirmRemoveHandler = null"
    >
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header">
            <h2 class="modal-title">Remove handler</h2>
            <button type="button" class="btn-close" @click="confirmRemoveHandler = null"></button>
          </div>
          <div class="modal-body">
            Remove the <code>{{ confirmRemoveHandler }}</code> handler and everything in it?
            Removals stay local until you save changes.
          </div>
          <div class="modal-footer">
            <button type="button" class="bb-btn-cancel" @click="confirmRemoveHandler = null">Cancel</button>
            <button type="button" class="btn btn-danger" @click="removeHandler(confirmRemoveHandler!)">Remove</button>
          </div>
        </div>
      </div>
    </div>
    <div v-if="confirmRemoveHandler !== null" class="modal-backdrop fade show"></div>
  </div>
</template>
